/**
 * @ Author: Housefolios
 * @ Create Time: 2021-09-16 13:21:50
 * @ Modified by: David Helmick
 * @ Modified time: 2025-05-08 16:57:21
 * @ Description: Dialog for sharing a portfolio with another through email
 */

import React, { useState } from 'react'
import ShareIcon from '@mui/icons-material/Share'
import {
  Grid,
  Avatar,
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  IconButton,
  ListItemButton,
  MenuItem,
  TextField,
  Typography,
  Box,
} from '@mui/material'

// Centralized lodash imports - much cleaner!
import { map, head, isEmpty, filter, get, find } from '@/utils/lodash'

import { enqueueSnackbar } from 'notistack'
import withSession from '@/housefolios-components/Session/withSession'
import {
  TextValidator,
  ValidatorForm,
} from '@/housefolios-components/shared/muiFormValidator'
import { GET_ORGS_PORTFOLIO_SHARED_WITH } from '../queries'
import { useMutation, useQuery } from '@apollo/client/react'
import {
  REMOVE_SHARED_PORTFOLIO,
  SHARE_PORTFOLIO,
} from '../mutations'
import BlockUi from 'react-block-ui'
import { PacmanLoader } from 'react-spinners'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import RemoveSharedPortfolio from '../RemoveSharedPortfolio'
import UpdateSharedPortfolio from '../UpdateSharedPortfolio'
import { useShareEmails } from '@/hooks/useShareEmails'
import { LoadingSkeleton } from '@/housefolios-components/LoadingSkeleton'
import ShareEmailsAutocomplete from '@/housefolios-components/FormComponents/ShareEmailsAutocomplete'

function SharePortfolioComponent(props) {
  const { sidebarMenu, portfolio = {} } = props

  const shareEmails = useShareEmails()
  const [openSharePortfolio, setOpenSharePortfolio] = useState(false)
  const [openUnsharePortfolio, setOpenUnsharePortfolio] =
    useState(null)
  const [sharePermission, setSharePermission] = useState('viewOnly')
  const [loader, setLoader] = useState(false)
  const handleOpenSharePortfolio = () => {
    shareEmails.resetEmails()
    setOpenSharePortfolio(true)
  }
  const handleCloseSharePortfolio = () => {
    setOpenSharePortfolio(false)
    setLoader(false)
  }

  const handleOpenUnsharePortfolio = (id) => {
    setOpenUnsharePortfolio(id)
  }

  const handleCloseUnsharePortfolio = () => {
    setOpenUnsharePortfolio(null)
  }

  const [sharePortfolio] = useMutation(SHARE_PORTFOLIO)
  const [removeSharedPortfolio] = useMutation(REMOVE_SHARED_PORTFOLIO)

  const handleShare = () => {
    const finalEmails = shareEmails.collectEmails()

    if (isEmpty(finalEmails)) {
      enqueueSnackbar('Enter at least one valid email address', {
        variant: 'warning',
        autoHideDuration: 4000,
      })
      return
    }

    setLoader(true)
    sharePortfolio({
      variables: {
        id: portfolio._id,
        emails: finalEmails,
        sharePermission,
      },
    })
      .then(() => {
        enqueueSnackbar(`Email Sent`, {
          variant: 'success',
          autoHideDuration: 3000,
        })
        handleCloseSharePortfolio()
      })
      .catch((error) => {
        enqueueSnackbar(error?.message || `Failed to send email`, {
          variant: 'error',
          autoHideDuration: 4000,
        })
        setLoader(false)
      })
  }

  const { loading, error, data } = useQuery(
    GET_ORGS_PORTFOLIO_SHARED_WITH,
    {
      variables: {
        portfolioId: portfolio._id,
      },
    },
  )

  if (loading) return <LoadingSkeleton variant="menuItem" />
  if (error) return `Error! ${error.message}`
  const { orgsPortfolioSharedWith } = data

  return (
    <>
      {sidebarMenu && (
        <ListItemButton onClick={handleOpenSharePortfolio}>
          <ShareIcon style={{ marginRight: 5, color: '#757575' }} />
          <span>Share Portfolio</span>
        </ListItemButton>
      )}

      <Dialog
        open={openSharePortfolio}
        onClose={handleCloseSharePortfolio}
        maxWidth="sm"
        fullWidth={true}
        scroll="body"
      >
        <DialogTitle>Share {portfolio.name} portfolio</DialogTitle>
        <BlockUi
          blocking={loader}
          loader={
            <PacmanLoader color={'var(--primary)'} loading={true} />
          }
          message={
            <Box sx={{ color: '#368cff' }}>Sharing Portfolio</Box>
          }
        >
          <ValidatorForm
            onSubmit={(event) => {
              event.preventDefault()
              handleShare()
            }}
            style={{
              overflowY: 'auto',
              display: 'flex',
              flexDirection: 'column',
            }}
          >
            <DialogContent>
              <Grid container direction="row">
                <Grid size={12}>
                  <ShareEmailsAutocomplete
                    shareEmails={shareEmails}
                  />
                  {/* <TextValidator
                                          name="email"
                                          type="email"
                                          variant='outlined'
                                          margin='dense'
                                          autoFocus
                                          onChange={event => setEmail(event.target.value)}
                                          value={email}
                                          placeholder="Enter email address..."
                                          label="Email"
                                          fullWidth={true}
                                          validators={[
                                          'required',
                                          'maxStringLength: 50',
                                          'isEmail',
                                          ]}
                                          errorMessages={[
                                          'This field is required',
                                          'You cannot exceed 50 characters',
                                          'Not a valid email address',
                                          ]}
                                      /> */}
                </Grid>
                <Grid size={12}>
                  <TextField
                    select
                    name="sharePermission"
                    variant="outlined"
                    margin="dense"
                    label="Permission"
                    fullWidth={true}
                    // style={{ width: 175 }}
                    value={sharePermission}
                    onChange={(event) =>
                      setSharePermission(event.target.value)
                    }
                    required={true}
                  >
                    <MenuItem key={'viewOnly'} value={'viewOnly'}>
                      {'View Only'}
                    </MenuItem>
                    <MenuItem key={'canEdit'} value={'canEdit'}>
                      {'Can Edit'}
                    </MenuItem>
                  </TextField>
                </Grid>
                <Grid size={12} style={{ padding: 10 }}>
                  {!isEmpty(orgsPortfolioSharedWith) && (
                    <Grid container spacing={2}>
                      <Grid size={12}>
                        <Typography>Shared With</Typography>
                      </Grid>
                      {map(orgsPortfolioSharedWith, (org) => (
                          <Grid size={12} key={org._id || org.email || org.name}>
                            <Grid
                              container
                              direction="row"
                              spacing={2}
                              sx={{
                                alignItems: 'center',
                              }}
                            >
                              <Grid>
                                <Avatar>{head(org.name)}</Avatar>
                              </Grid>
                              <Grid>
                                <Grid container direction="column" sx={{ lineHeight: "1.3" }}>
                                  <Grid>
                                    <Box component="b" sx={{ fontSize: "1.15rem !important" }}>
                                      {org.name}
                                    </Box>
                                  </Grid>
                                  <Grid>
                                    <Box
                                      component="span"
                                      sx={{
                                        color: 'rgba(7, 9, 25, 0.5)',
                                        display: 'block',
                                      }}
                                    >
                                      {org.email}
                                    </Box>
                                  </Grid>
                                  <Grid>
                                    <Box
                                      component="span"
                                      sx={{
                                        color: 'rgba(7, 9, 25, 0.5)',
                                        display: 'block',
                                      }}
                                    >
                                      {get(
                                        find(org.sharedPortfolios, [
                                          '_id',
                                          portfolio._id,
                                        ]),
                                        'sharePermission',
                                      ) === 'canEdit'
                                        ? 'Can Edit'
                                        : 'View Only'}
                                    </Box>
                                  </Grid>
                                </Grid>
                              </Grid>
                              <Typography style={{ flex: 1 }} />
                              <Grid>
                                <UpdateSharedPortfolio
                                  orgId={org._id}
                                  portfolioId={portfolio._id}
                                  sharePermission={get(
                                    find(org.sharedPortfolios, [
                                      '_id',
                                      portfolio._id,
                                    ]),
                                    'sharePermission',
                                  )}
                                  shareAccess
                                />
                              </Grid>
                              <Grid>
                                <RemoveSharedPortfolio
                                  orgId={org._id}
                                  portfolioId={portfolio._id}
                                  shareAccess
                                />
                              </Grid>
                            </Grid>
                          </Grid>
                      ))}
                    </Grid>
                  )}
                </Grid>
              </Grid>
            </DialogContent>
            <DialogActions>
              <Button
                variant="text"
                size="small"
                onClick={handleCloseSharePortfolio}
              >
                <Typography>CANCEL</Typography>
              </Button>
              <Button
                variant="contained"
                color="primary"
                size="small"
                type="submit"
                disabled={loader}
              >
                <Typography>SHARE</Typography>
              </Button>
            </DialogActions>
          </ValidatorForm>
        </BlockUi>
      </Dialog>
    </>
  )
}
export default withSession(SharePortfolioComponent)
